import { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import Spinner from "../../spinner/Spinner";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../../components/utils/CustomStyles";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import formatDate from "../../../components/utils/DateFormatter";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { useProcessedRemittances } from "../../../hooks/useAppData";

const GetProcessRemittance = () => {
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const { permissions } = useContext(AuthContext);
  const navigate = useNavigate();

  const { data: remittanceResponse, isLoading: loading } = useProcessedRemittances();
  const processRemittance = (remittanceResponse?.data?.remittances || remittanceResponse?.data || []).slice().reverse();

  const handleFilter = (e) => {
    setFilterText(e.target.value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const handleViewDetails = (row) => {
    navigate("/operations/remittance/process/details", { state: row });
  };

  const ExpandableComponent = ({ data }) => {
    return (
      <div className="flex justify-start p-4 relative">
        {permissions.some(permission => permission.name === 'branch.remittance.view') && (
          <>
            <div className="mx-2 my-2 group relative">
              <Link to="/operations/branch/remittance/process/details" state={{ remittanceData: data }}>
                <Eye className="w-5 h-5 text-darkGreen3" />
              </Link>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-darkGreen3 text-xs px-2 py-1 rounded">
                Details
              </div>
            </div>
          </>
        )}

      </div>
    );
  };

  const columns = [
    {
      name: "Remittance Number",
      selector: (row) => row.remittance_number || "NOT AVAILABLE",
      sortable: true,
      width: "240px",
    },
    {
      name: "Type",
      selector: (row) => row.type_label || "NOT AVAILABLE",
      sortable: true,
      width: "200px",
    },
    {
      name: "Branch",
      selector: (row) => row.branch_name || "NOT AVAILABLE",
      sortable: true,
      width: "220px",
    },
    {
      name: "Amount",
      selector: (row) => `₦${CurrencyFormatter(row.amount)}` || "₦0.00",
      sortable: true,
      width: "160px",
    },
    {
      name: "Description",
      selector: (row) => row.description || "NOT AVAILABLE",
      sortable: true,
      width: "260px",
    },
    {
      name: "Branch Note",
      selector: (row) => row.branch_note || "NOT AVAILABLE",
      sortable: true,
      width: "260px",
    },
    {
      name: "Rejection Reason",
      selector: (row) => row.rejection_reason || "—",
      sortable: true,
      width: "260px",
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`${row.status === "pending"
            ? "text-orange1"
            : row.status === "approved"
              ? "text-darkGreen3"
              : row.status === "rejected"
                ? "text-red-500"
                : "text-gray-500"
            } font-semibold`}
        >
          {row.status}
        </span>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "Submitted At",
      selector: (row) =>
        row.submitted_at ? formatDate(row.submitted_at) : "NOT AVAILABLE",
      sortable: true,
      width: "180px",
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          onClick={() => handleViewDetails(row)}
          className="bg-darkGreen3 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition duration-200"
        >
          View Details
        </button>
      ),
      width: "160px",
    },
  ];

  const filteredItems = processRemittance.filter((item) =>
    Object.values(item || {})
      .join(" ")
      .toLowerCase()
      .includes(filterText.toLowerCase())
  );

  const data = filteredItems.map((remittance) => ({
    id: remittance.id,
    remittance_number: remittance.remittance_number || "NOT AVAILABLE",
    type: remittance.type || "NOT AVAILABLE",
    type_label: remittance.type_label || "NOT AVAILABLE",
    amount: remittance.amount || "NOT AVAILABLE",
    description: remittance.description || "NOT AVAILABLE",
    branch_note: remittance.branch_note || "NOT AVAILABLE",
    status: remittance.status || "NOT AVAILABLE",
    rejection_reason: remittance.rejection_reason || "NOT AVAILABLE",
    reference: remittance.reference || "NOT AVAILABLE",
    submitted_at: remittance.submitted_at || "NOT AVAILABLE",
    approved_at: remittance.approved_at || "NOT AVAILABLE",
    completed_at: remittance.completed_at || "NOT AVAILABLE",
    created_at: remittance.created_at || "NOT AVAILABLE",
    time_ago: remittance.time_ago || "NOT AVAILABLE",
    branch_id: remittance.branch?.id || "NOT AVAILABLE",
    branch_name: remittance.branch?.name || "NOT AVAILABLE",
    branch_email: remittance.branch?.email || "NOT AVAILABLE",
    branch_phone: remittance.branch?.phone_number || "NOT AVAILABLE",
    approver_id: remittance.approver?.id || "NOT AVAILABLE",
    approver_name: remittance.approver?.fullname || "NOT AVAILABLE",
    approver_email: remittance.approver?.email || "NOT AVAILABLE",
  }));

  return (
    <MainLayouts>
      <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
        {/* Breadcrumb */}
        <section
          className="w-full pr-[3%] pt-3 my-3"
          style={{ backgroundColor: "#F7F7F7" }}
        >
          <nav className="bg-gray-200 py-0 px-4 rounded-lg">
            <ol className="list-none p-0 inline-flex mb-2">
              <li className="flex items-center">
                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                  Operations
                </p>
                <ChevronRightIcon />
              </li>
              <li className="flex items-center">
                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                  Remittance
                </p>
                <ChevronRightIcon />
              </li>
              <li className="flex items-center">
                <p className="text-orange1">Process</p>
              </li>
            </ol>
          </nav>
        </section>

        {/* Search Bar */}
        <div className="pb-4 bg-white flex flex-wrap justify-between">
          <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
            <input
              type="search"
              className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-darkGreen3 focus:border-transparent"
              placeholder="Search remittance by number, branch, description..."
              value={filterText}
              onChange={handleFilter}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <Spinner />
        ) : (
          <DataTable
            columns={columns}
            data={data}
            pagination
            highlightOnHover
            striped
            customStyles={CustomStyles}
            noDataComponent={
              <div className="py-8 text-center text-gray-500">
                No deducted remittance found
              </div>
            }
            expandableRows
            expandableRowsComponent={ExpandableComponent}
            selectableRows
            onSelectedRowsChange={({ selectedRows }) =>
              setSelectedRows(selectedRows)
            }
            persistTableHead
          />
        )}
      </div>
    </MainLayouts>
  );
};

export default GetProcessRemittance;
